import { ChangeDetectionStrategy, Component, NgZone, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GuidedTourDirective } from '../../guided-tour.directive';
import { GuidedTourWrapperComponent } from '../../guided-tour-wrapper.component';
import { myOrganizationSteps } from './my-organization-steps';
import { GuidedTourService } from '../../../service/guided-tour.service';
import { OA_ADMIN_AREA } from '../../../model/guided-tour';
import { Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'markant-platform-my-organization',
  standalone: true,
  imports: [CommonModule, GuidedTourWrapperComponent],
  templateUrl: './my-organization.component.html',
  styleUrl: './my-organization.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyOrganizationComponent extends GuidedTourDirective {
  @ViewChild(GuidedTourWrapperComponent) guidedTourComponent!: GuidedTourWrapperComponent;

  readonly myOrganizationSteps = myOrganizationSteps;
  enableClickThrough = true;

  nextButtonCallback = this.nextTarget.bind(this);
  backButtonCallback = this.previousTarget.bind(this);
  private stepDelay?: number;

  constructor(
    guidedTourService: GuidedTourService,
    protected override router: Router,
    private ngZone: NgZone
  ) {
    super(OA_ADMIN_AREA, 2, guidedTourService, router);
  }

  override async nextTarget(): Promise<void> {
    super.nextTarget();
    this.enableClickThrough = true;

    switch (this.currentStep) {
      case 1:
        await this.navigateTo('/organization');
        break;
      case 2:
        this.stepDelay = window.setTimeout(async () => {
          await this.nextTarget();
        }, 3000);
        break;
      case 3:
        this.cleanTimeoutAndFocus(this.stepDelay);
        break;
      case 6:
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        break;
      case 7:
        this.cleanTimeoutAndFocus(this.stepDelay);
        break;
    }

    if (this.currentStep === 1) {
      await this.guidedTourComponent.start();
    } else if (this.currentStep <= this.myOrganizationSteps.length) {
      await this.guidedTourComponent.next();
    } else {
      await this.guidedTourComponent.end();
      await this.completeSection(true);
    }
  }

  private async navigateTo(path: string): Promise<void> {
    await this.ngZone.run(() => this.router.navigate([path]));
    await firstValueFrom(of(true));
  }

  override async previousTarget(): Promise<void> {
    super.jumpToTarget(this.currentStep - 1);
    this.stepDelay && clearTimeout(this.stepDelay);

    if (this.currentStep === 0) {
      await this.router.navigate(['']);
      await this.guidedTourComponent.end();
      this.cancelSection();
      return;
    }

    if (myOrganizationSteps[this.currentStep - 1].highlightTargetId === 'markant-body') {
      super.jumpToTarget(this.currentStep - 1);
    }

    switch (this.currentStep) {
      case 1:
        await this.navigateTo('/organization');
        break;
      case 2:
        this.enableClickThrough = false;
        break;
      case 5:
        await this.navigateTo('/organization/organization-overview');
        break;
    }
    if (this.currentStep === 6) {
      await this.guidedTourComponent.jumpTo(this.currentStep - 2);
    } else {
      await this.guidedTourComponent.jumpTo(this.currentStep - 1);
    }
  }
}
